import { createInertiaApp } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

const standalonePages = [
  'Auth/Login',
  'Auth/Signup',
  'Auth/ForgotPassword',
  'Auth/ResetPassword',
  'Auth/AcceptInvitation',
  'Auth/InviteUser',
  'Lti/Dashboard',
  'Lti/Courses/Show',
  'Lti/Courses/Companion',
  'Lti/Courses/ScheduleMeeting'
]

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob('../pages/**/*.{jsx,tsx}')
    const resolver = pages[`../pages/${name}.jsx`] || pages[`../pages/${name}.tsx`]
    if (!resolver) {
      console.error(`Missing Inertia page component: '${name}'`)
      return
    }
    if (standalonePages.includes(name)) {
      return resolver()
    }
    // AppLayout is admin-only; lazy-import it so LTI users never fetch it.
    return Promise.all([ resolver(), import('../components/layouts/AppLayout') ])
      .then(([ page, { default: AppLayout } ]) => {
        page.default.layout ||= (pageContent) => <AppLayout>{pageContent}</AppLayout>
        return page
      })
  },

  setup({ el, App, props }) {
    const pageName = props.initialPage?.component || ""
    const isStandalone = standalonePages.includes(pageName)

    // Standalone pages (LTI, Auth) don't use dark mode
    if (isStandalone) {
      document.documentElement.classList.remove("dark")
    }

    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_javascript_tag "inertia.jsx" %> to the Inertia-specific layout instead.',
    )
  }
})
